'use client'

import * as React from 'react'
import { cn } from '@shared/utils'
import { useFormField } from '../../hooks/useFormField'
import { useErrorHandling } from '../../utils/useErrorHandling'

/**
 * @group Components
 * 
 * @summary
 * <blockquote>Form description component that displays help text for form fields.</blockquote>
 * 
 * @example
 * ```typescript
 * import { FormDescription } from '@shared/ui'
 * 
 * <FormDescription>
 *   Enter your email address to receive updates.
 * </FormDescription>
 * ```
 */
export const FormDescription = React.forwardRef<
  HTMLParagraphElement,
  React.ComponentPropsWithoutRef<'p'>
>(({ className, ...props }, ref) => {
  const field = useFormField()
  const { showContextError } = useErrorHandling()

  if (!field) {
    showContextError('FormDescription')
    return null
  }

  const { formDescriptionId } = field

  return (
    <p
      ref={ref}
      id={formDescriptionId}
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  )
})
FormDescription.displayName = 'FormDescription' 